import { Variants } from 'framer-motion';

/* ────────────────────────────────────────────────────────────────────── */
/* PAGE TRANSITIONS                                                        */
/* ────────────────────────────────────────────────────────────────────── */

export const pageVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.23, 1, 0.32, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.5,
      ease: [0.23, 1, 0.32, 1],
    },
  },
};

/* ────────────────────────────────────────────────────────────────────── */
/* HERO ANIMATIONS                                                         */
/* ────────────────────────────────────────────────────────────────────── */

export const letterVariants: Variants = {
  hidden: { opacity: 0, y: 60 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.1 + i * 0.045,
      duration: 0.65,
      ease: [0.34, 1.56, 0.64, 1],
    },
  }),
};

export const heroSubVariants: Variants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.8,
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export const heroBtnsVariants: Variants = {
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 1.0,
      duration: 0.55,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

/* ────────────────────────────────────────────────────────────────────── */
/* SCROLL-TRIGGERED ANIMATIONS                                            */
/* ────────────────────────────────────────────────────────────────────── */

export const revealVariants: Variants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export const staggerContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.2,
    },
  },
};

export const staggerItemVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

/* ────────────────────────────────────────────────────────────────────── */
/* CARD ANIMATIONS                                                         */
/* ────────────────────────────────────────────────────────────────────── */

export const cardVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.55,
      ease: [0.16, 1, 0.3, 1],
    },
  },
  whileHover: {
    y: -8,
    boxShadow: '0 20px 40px rgba(92, 225, 230, 0.15)',
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
};

export const skillCardVariants: Variants = {
  hidden: { opacity: 0, x: -30 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
  whileHover: {
    x: 8,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
};

export const projectRowVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.55,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
  whileHover: {
    x: 12,
    backgroundColor: 'rgba(92, 225, 230, 0.05)',
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
};

/* ────────────────────────────────────────────────────────────────────── */
/* BUTTON ANIMATIONS                                                       */
/* ────────────────────────────────────────────────────────────────────── */

export const buttonVariants: Variants = {
  rest: {
    scale: 1,
    boxShadow: '0 0 0 rgba(92, 225, 230, 0.3)',
  },
  hover: {
    scale: 1.05,
    boxShadow: '0 8px 24px rgba(92, 225, 230, 0.4)',
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  tap: {
    scale: 0.98,
  },
};

/* ────────────────────────────────────────────────────────────────────── */
/* PARALLAX SCROLL                                                         */
/* ────────────────────────────────────────────────────────────────────── */

export const parallaxVariants = (offset: number = -30): Variants => ({
  hidden: { y: offset },
  visible: {
    y: 0,
    transition: {
      duration: 1,
      ease: 'easeOut',
    },
  },
});

/* ────────────────────────────────────────────────────────────────────── */
/* SCROLL INDICATOR                                                        */
/* ────────────────────────────────────────────────────────────────────── */

export const scrollIndicatorVariants: Variants = {
  animate: {
    y: [0, 8, 0],
    opacity: [1, 0.5, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

/* ────────────────────────────────────────────────────────────────────── */
/* MODAL / OVERLAY                                                         */
/* ────────────────────────────────────────────────────────────────────── */

export const modalVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      ease: 'easeIn',
    },
  },
};

export const modalContentVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.23, 1, 0.32, 1],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.3,
      ease: 'easeIn',
    },
  },
};

/* ────────────────────────────────────────────────────────────────────── */
/* NAV ANIMATIONS                                                          */
/* ────────────────────────────────────────────────────────────────────── */

export const navLinkVariants: Variants = {
  rest: { color: 'rgba(240, 248, 255, 0.7)' },
  hover: {
    color: 'rgba(92, 225, 230, 1)',
    textShadow: '0 0 10px rgba(92, 225, 230, 0.6)',
    transition: {
      duration: 0.3,
    },
  },
};

/* ────────────────────────────────────────────────────────────────────── */
/* GRADIENT ANIMATIONS                                                     */
/* ────────────────────────────────────────────────────────────────────── */

export const gradientVariants: Variants = {
  animate: {
    backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
    transition: {
      duration: 15,
      repeat: Infinity,
      ease: 'linear',
    },
  },
};

export const glowVariants: Variants = {
  animate: {
    boxShadow: [
      '0 0 20px rgba(92, 225, 230, 0.3)',
      '0 0 40px rgba(92, 225, 230, 0.6)',
      '0 0 20px rgba(92, 225, 230, 0.3)',
    ],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};
